import React, { useState, useEffect, useRef } from 'react';
import { Receipt, ChevronLeft, ChevronRight, AlertCircle, FileText, DollarSign, RefreshCw, Wallet, Download, X, CheckSquare, Square, Calendar, Send, Users } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import BetaPaymentsModal from '../components/BetaPaymentsModal';
import ProductPaymentsModal from '../components/ProductPaymentsModal';
import ApplyProductSalePaymentModal from '../components/ApplyProductSalePaymentModal';
import RecordPaymentModal from '../components/RecordPaymentModal';
import PayoutDetailsModal from '../components/PayoutDetailsModal';
import SendStatementModal from '../components/SendStatementModal';
import PaymentsLedgerTab from '../components/invoices/PaymentsLedgerTab';
import InvoicesLedgerTab from '../components/invoices/InvoicesLedgerTab';
import InvoiceEditorModal from '../components/invoices/InvoiceEditorModal';
import AccountsReceivableTab from '../components/invoices/AccountsReceivableTab';
import ReceivePaymentModal from '../components/invoices/ReceivePaymentModal';
import SubscriptionsPanel from '../components/subscriptions/SubscriptionsPanel';
import { parseCalendarDate, formatCalendarDate, getCalendarDaysPastDue } from '../lib/invoiceDates';
import { getBooking } from '../lib/bookings';

export default function Invoices() {
  const { selectedBusiness } = useBusiness();
  const [activeTab, setActiveTab] = useState<'invoices' | 'payments' | 'receivables' | 'payouts' | 'subscriptions'>('invoices');
  const [subscriptionsEnabled, setSubscriptionsEnabled] = useState(false);
  const invoiceTabsRef = useRef<HTMLDivElement>(null);
  const [arRefreshKey, setArRefreshKey] = useState(0);
  const [receivePaymentFor, setReceivePaymentFor] = useState<{ id: string; name: string } | null>(null);
  const [bookings, setBookings] = useState<any[]>([]);
  const [productSales, setProductSales] = useState<any[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [isPaymentsModalOpen, setIsPaymentsModalOpen] = useState(false);
  const [isInvoiceEditorOpen, setIsInvoiceEditorOpen] = useState(false);
  const [invoiceEditorId, setInvoiceEditorId] = useState<string | null>(null);
  const [invoiceEditorRefreshKey, setInvoiceEditorRefreshKey] = useState(0);
  const [standaloneViewId, setStandaloneViewId] = useState<string | null>(null);
  const [isProductPaymentsModalOpen, setIsProductPaymentsModalOpen] = useState(false);
  const [selectedProductSaleForView, setSelectedProductSaleForView] = useState<any>(null);
  const [isApplyPaymentModalOpen, setIsApplyPaymentModalOpen] = useState(false);
  const [selectedProductSale, setSelectedProductSale] = useState<any>(null);
  const [isRecordPaymentModalOpen, setIsRecordPaymentModalOpen] = useState(false);
  const [selectedBookingForPayment, setSelectedBookingForPayment] = useState<any>(null);
  // Invoice-backed product sale (counter sale / order) getting a quick payment.
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<any>(null);
  const [dateFilter, setDateFilter] = useState<'this-month' | 'last-month' | 'this-year' | 'last-year' | 'all-time' | 'custom'>('this-month');
  const [statusFilter, setStatusFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [customDateStart, setCustomDateStart] = useState('');
  const [customDateEnd, setCustomDateEnd] = useState('');

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);
  const [bulkDownloadProgress, setBulkDownloadProgress] = useState('');

  // Transactions/Payouts state
  const [transactions, setTransactions] = useState<any[]>([]);
  const [filteredTransactions, setFilteredTransactions] = useState<any[]>([]);
  const [isLoadingTransactions, setIsLoadingTransactions] = useState(false);
  const [isSyncingTransactions, setIsSyncingTransactions] = useState(false);
  const [payoutDateFilter, setPayoutDateFilter] = useState<'this-month' | 'last-month' | 'this-year' | 'all-time'>('this-month');
  const [payoutStatusFilter, setPayoutStatusFilter] = useState<'all' | 'pending' | 'in_transit' | 'paid' | 'failed'>('all');
  const [selectedPayout, setSelectedPayout] = useState<any>(null);
  const [isPayoutDetailsModalOpen, setIsPayoutDetailsModalOpen] = useState(false);
  const [isStatementModalOpen, setIsStatementModalOpen] = useState(false);

  // Date filtering function with timezone handling
  const getDateRange = (filter: string) => {
    const now = new Date();
    
    // Helper to create date at midnight in local timezone
    const createDateAtMidnight = (year: number, month: number, day: number) => {
      return new Date(year, month, day, 0, 0, 0, 0);
    };
    
    // Helper to get end of day (23:59:59.999)
    const createDateAtEndOfDay = (year: number, month: number, day: number) => {
      return new Date(year, month, day, 23, 59, 59, 999);
    };

    let result;
    switch (filter) {
      case 'this-month':
        const startOfThisMonth = createDateAtMidnight(now.getFullYear(), now.getMonth(), 1);
        const endOfToday = createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate());
        result = {
          start: startOfThisMonth,
          end: endOfToday
        };
        break;
      case 'last-month':
        const lastMonth = now.getMonth() === 0 ? 11 : now.getMonth() - 1;
        const lastMonthYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        const startOfLastMonth = createDateAtMidnight(lastMonthYear, lastMonth, 1);
        // Get last day of last month
        const lastDayOfLastMonth = new Date(lastMonthYear, lastMonth + 1, 0).getDate();
        const endOfLastMonth = createDateAtEndOfDay(lastMonthYear, lastMonth, lastDayOfLastMonth);
        result = {
          start: startOfLastMonth,
          end: endOfLastMonth
        };
        break;
      case 'this-year':
        const startOfThisYear = createDateAtMidnight(now.getFullYear(), 0, 1);
        const endOfTodayYear = createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate());
        result = {
          start: startOfThisYear,
          end: endOfTodayYear
        };
        break;
      case 'last-year':
        const startOfLastYear = createDateAtMidnight(now.getFullYear() - 1, 0, 1);
        const endOfLastYear = createDateAtEndOfDay(now.getFullYear() - 1, 11, 31);
        result = {
          start: startOfLastYear,
          end: endOfLastYear
        };
        break;
      case 'custom':
        if (customDateStart && customDateEnd) {
          const [sy, sm, sd] = customDateStart.split('-').map(Number);
          const [ey, em, ed] = customDateEnd.split('-').map(Number);
          result = {
            start: createDateAtMidnight(sy, sm - 1, sd),
            end: createDateAtEndOfDay(ey, em - 1, ed)
          };
        } else {
          result = { start: new Date(0), end: createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate()) };
        }
        break;
      case 'all-time':
      default:
        result = {
          start: new Date(0),
          end: createDateAtEndOfDay(now.getFullYear(), now.getMonth(), now.getDate())
        };
    }
    
    return result;
  };

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    let cancelled = false;
    supabase
      .rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'services',
        p_setting_key: 'enable_subscriptions',
        p_default_value: false
      })
      .then(({ data }) => {
        if (!cancelled && data !== null && data !== undefined) setSubscriptionsEnabled(!!data);
      });

    const handleSettingChange = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.key === 'enable_subscriptions') setSubscriptionsEnabled(!!detail.value);
    };
    window.addEventListener('servicesSettingChanged', handleSettingChange);
    return () => {
      cancelled = true;
      window.removeEventListener('servicesSettingChanged', handleSettingChange);
    };
  }, [selectedBusiness?.id]);

  useEffect(() => {
    if (!subscriptionsEnabled && activeTab === 'subscriptions') setActiveTab('invoices');
  }, [subscriptionsEnabled, activeTab]);

  useEffect(() => {
    const bar = invoiceTabsRef.current;
    const active = bar?.querySelector<HTMLButtonElement>('[data-tab-active="true"]');
    if (!bar || !active) return;
    const barRect = bar.getBoundingClientRect();
    const btnRect = active.getBoundingClientRect();
    if (btnRect.left < barRect.left) {
      bar.scrollLeft -= barRect.left - btnRect.left + 6;
    } else if (btnRect.right > barRect.right) {
      bar.scrollLeft += btnRect.right - barRect.right + 6;
    }
  }, [activeTab, subscriptionsEnabled]);

  // Check for invoiceId in URL parameters on mount
  useEffect(() => {
    const openInvoiceById = (invoiceId: string): boolean => {
      const bookingInvoice = bookings.find(b => b.id === invoiceId);
      const productSaleInvoice = productSales.find(ps => ps.id === invoiceId);

      if (bookingInvoice) {
        setSelectedInvoice({ ...bookingInvoice, type: 'booking' });
        setIsPaymentsModalOpen(true);
        return true;
      }
      if (productSaleInvoice) {
        if ((productSaleInvoice as any).invoice_id) {
          setStandaloneViewId((productSaleInvoice as any).invoice_id);
        } else {
          setSelectedProductSaleForView(productSaleInvoice);
          setIsProductPaymentsModalOpen(true);
        }
        return true;
      }
      return false;
    };

    const urlParams = new URLSearchParams(window.location.search);
    const invoiceId = urlParams.get('invoiceId');
    if (invoiceId && openInvoiceById(invoiceId)) {
      window.history.replaceState({}, document.title, '/invoices');
    }

    // AI assistant deep link when the page is already mounted (the URL-param
    // path above only fires when bookings/productSales load).
    const onAssistantOpen = (e: Event) => {
      const path = (e as CustomEvent).detail?.path as string | undefined;
      if (!path) return;
      const id = new URLSearchParams(path.split('?')[1] || '').get('invoiceId');
      if (id) openInvoiceById(id);
    };
    window.addEventListener('assistant:openInvoice', onAssistantOpen);
    return () => window.removeEventListener('assistant:openInvoice', onAssistantOpen);
  }, [bookings, productSales]);

  // Combine and filter invoices (bookings + product sales) based on date range
  useEffect(() => {
    // Combine bookings and product sales into unified invoice list
    const allInvoices = [
      ...bookings.map(booking => ({
        ...booking,
        type: 'booking',
        invoice_number: booking.booking_number,
        invoice_date: booking.end_date || booking.created_at,
        invoice_total: booking.total_amount
      })),
      ...productSales.map(sale => ({
        ...sale,
        type: 'product_sale',
        // Invoice-backed sales are the real invoice — show its number, not the
        // internal sale number.
        invoice_number: (sale as any).invoice?.invoice_number || sale.sale_number,
        invoice_date: sale.created_at,
        invoice_total: sale.total_amount
      }))
    ].sort((a, b) => {
      // Sort by date descending (newest first) — parse as local calendar dates
      // so date-only end_date values don't shift across the UTC boundary.
      const dateA = parseCalendarDate(a.invoice_date)?.getTime() ?? 0;
      const dateB = parseCalendarDate(b.invoice_date)?.getTime() ?? 0;
      return dateB - dateA;
    });

    // Apply date filter
    let dateFiltered = allInvoices;
    if (dateFilter !== 'all-time' && !(dateFilter === 'custom' && (!customDateStart || !customDateEnd))) {
      const { start, end } = getDateRange(dateFilter);
      dateFiltered = allInvoices.filter(invoice => {
        // Use local calendar date so a date-only end_date (e.g. "2026-06-01")
        // isn't pushed into the previous day by UTC parsing — which was dropping
        // first-of-month invoices and mislabelling dates.
        const invoiceDate = parseCalendarDate(invoice.invoice_date);
        if (!invoiceDate) return false;
        return invoiceDate >= start && invoiceDate <= end;
      }).sort((a, b) => {
        // Sort filtered results by date descending (newest first)
        const dateA = parseCalendarDate(a.invoice_date)?.getTime() ?? 0;
        const dateB = parseCalendarDate(b.invoice_date)?.getTime() ?? 0;
        return dateB - dateA;
      });
    }
    
    // Apply status filter (always applies regardless of date filter)
    const statusFiltered = statusFilter === 'all' ? dateFiltered : dateFiltered.filter(invoice => {
      const paymentStatus = getPaymentStatus(invoice);
      return statusFilter === 'paid' ? paymentStatus.status === 'paid' : paymentStatus.status !== 'paid';
    });
    
    setFilteredInvoices(statusFiltered);
  }, [bookings, productSales, dateFilter, statusFilter, customDateStart, customDateEnd]);

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [dateFilter, statusFilter, customDateStart, customDateEnd]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredInvoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredInvoices.map(i => i.id)));
    }
  };

  const handleBulkDownload = async () => {
    if (selectedIds.size === 0) return;
    setIsBulkDownloading(true);
    setBulkDownloadProgress('Preparing...');

    try {
      const selected = filteredInvoices.filter(i => selectedIds.has(i.id));
      const results: { filename: string; base64: string }[] = [];
      const BATCH = 5;

      for (let i = 0; i < selected.length; i += BATCH) {
        const batch = selected.slice(i, i + BATCH);
        setBulkDownloadProgress(`Generating ${Math.min(i + BATCH, selected.length)} of ${selected.length}...`);

        const batchResults = await Promise.all(
          batch.map(async (inv) => {
            // Invoice-backed sales render the real invoice PDF, not the legacy sale layout.
            const body = inv.type === 'booking'
              ? { bookingId: inv.id }
              : inv.invoice_id
              ? { invoiceId: inv.invoice_id }
              : { productSaleId: inv.id };
            const { data, error } = await supabase.functions.invoke('generate-invoice-pdf', { body });
            if (error || !data) return null;
            const fallbackName = inv.type === 'booking'
              ? `invoice-${inv.booking_number}.pdf`
              : inv.invoice_id
              ? `invoice-${inv.invoice_number}.pdf`
              : `product-sale-${inv.sale_number}.pdf`;
            return { filename: data.filename || fallbackName, base64: data.base64 };
          })
        );
        results.push(...batchResults.filter(Boolean) as { filename: string; base64: string }[]);
      }

      if (results.length === 0) {
        alert('No PDFs could be generated.');
        return;
      }

      const { Capacitor } = await import('@capacitor/core');
      const isNative = Capacitor.isNativePlatform();

      if (results.length === 1) {
        const r = results[0];
        const bin = atob(r.base64);
        const bytes = new Uint8Array(bin.length);
        for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
        const blob = new Blob([bytes], { type: 'application/pdf' });

        if (isNative) {
          const file = new File([blob], r.filename, { type: 'application/pdf' });
          if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: r.filename }).catch(() => {});
          }
        } else {
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = r.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      } else {
        setBulkDownloadProgress('Creating ZIP...');
        const JSZip = (await import('jszip')).default;
        const zip = new JSZip();
        for (const r of results) {
          const bin = atob(r.base64);
          const bytes = new Uint8Array(bin.length);
          for (let j = 0; j < bin.length; j++) bytes[j] = bin.charCodeAt(j);
          zip.file(r.filename, bytes);
        }
        const zipBlob = await zip.generateAsync({ type: 'blob' });

        if (isNative) {
          const file = new File([zipBlob], 'invoices.zip', { type: 'application/zip' });
          if (typeof navigator.share === 'function' && navigator.canShare?.({ files: [file] })) {
            await navigator.share({ files: [file], title: 'Invoices' }).catch(() => {});
          }
        } else {
          const url = URL.createObjectURL(zipBlob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'invoices.zip';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }
      }

      setSelectedIds(new Set());
    } catch (err) {
      console.error('Bulk download error:', err);
      alert('Failed to download invoices. Please try again.');
    } finally {
      setIsBulkDownloading(false);
      setBulkDownloadProgress('');
    }
  };

  // Fetch all bookings and product sales with payment data
  // Extract data fetching into a separate function for reuse
  const fetchInvoiceData = async () => {
    if (!selectedBusiness?.id) return;
    
    // Save scroll position before loading
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    sessionStorage.setItem('invoicesScrollPosition', scrollPosition.toString());
    
    setIsLoading(true);
    try {
      // Fetch bookings
      const { data: bookingsData, error: bookingsError } = await supabase
        .from('bookings')
        .select(`
          id,
          booking_number,
          total_amount,
          subtotal,
          tax_amount,
          discount_amount,
          status,
          location,
          notes,
          created_at,
          updated_at,
          end_date,
          customer:customers!bookings_customer_id_fkey(
            id,
            first_name,
            last_name,
            email,
            phone
          ),
          payments:payments_with_stripe_details(*),
          booking_services(
            id,
            service_id,
            name,
            description,
            price,
            tax_rate,
            tax_amount,
            total_amount,
            discount_amount,
            discount_type,
            discount_value,
            compare_at_price,
            duration_minutes,
            start_date,
            end_date,
            vehicle_id,
            add_ons:booking_service_addons(
              id,
              price,
              discount_type,
              discount_value,
              addon:service_addons(id, name, description)
            )
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'completed')
        .neq('is_deleted', true)
        .order('end_date', { ascending: false });

      if (bookingsError) throw bookingsError;
      setBookings(bookingsData || []);

      // Fetch product sales
      const { data: productSalesData, error: productSalesError } = await supabase
        .from('product_sales')
        .select(`
          id,
          sale_number,
          total_amount,
          subtotal_amount,
          tax_amount,
          tax_rate,
          discount_amount,
          payment_status,
          business_id,
          customer_id,
          invoice_id,
          created_at,
          invoice:invoices!product_sales_invoice_id_fkey(invoice_number, status),
          customer:customers!product_sales_customer_id_fkey(
            id,
            first_name,
            last_name,
            email,
            phone
          ),
          payments(*),
          items:product_sale_items(
            id,
            product_name,
            quantity,
            unit_price,
            total_price
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (productSalesError) throw productSalesError;
      setProductSales(productSalesData || []);

    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInvoiceData();
  }, [selectedBusiness]);

  // Restore scroll position after invoices list renders
  useEffect(() => {
    if (!isLoading && filteredInvoices.length > 0) {
      const savedScrollPosition = sessionStorage.getItem('invoicesScrollPosition');
      if (savedScrollPosition) {
        // Use a small delay to ensure DOM has updated
        setTimeout(() => {
          window.scrollTo({
            top: parseInt(savedScrollPosition, 10),
            behavior: 'instant'
          });
          sessionStorage.removeItem('invoicesScrollPosition');
        }, 50);
      }
    }
  }, [isLoading, filteredInvoices]);

  // Calculate payment status for an invoice (booking or product sale)
  const getPaymentStatus = (invoice: any) => {
    // Calculate based on payments (this is the source of truth)
    const totalAmount = invoice.invoice_total || invoice.total_amount || 0;
    const completedPayments = invoice.payments?.filter((p: any) => p.status === 'completed') || [];
    const totalPaid = completedPayments.reduce((sum: number, payment: any) => sum + payment.amount, 0);
    const balanceOwing = Math.round(Math.max(0, totalAmount - totalPaid) * 100) / 100;
    
    // Determine status based on payments
    let calculatedStatus: 'paid' | 'partial' | 'unpaid';
    if (balanceOwing === 0 && (totalPaid > 0 || totalAmount === 0)) {
      calculatedStatus = 'paid';
    } else if (totalPaid > 0) {
      calculatedStatus = 'partial';
    } else {
      calculatedStatus = 'unpaid';
    }
    
    // For product sales, check payment_status field but use calculated status if it's more accurate
    if (invoice.type === 'product_sale') {
      // If payment_status says paid, trust it
      if (invoice.payment_status === 'paid') {
        return { status: 'paid', label: 'Paid', color: 'green' };
      }
      // If payment_status says partial, trust it
      if (invoice.payment_status === 'partial') {
        return { status: 'partial', label: 'Partial', color: 'orange' };
      }
      // Otherwise, use calculated status from payments array (more reliable)
      // This handles cases where payment_status wasn't updated but payments exist
    }
    
    // Return calculated status
    if (calculatedStatus === 'paid') return { status: 'paid', label: 'Paid', color: 'green' };
    if (calculatedStatus === 'partial') return { status: 'partial', label: 'Partial', color: 'orange' };
    return { status: 'unpaid', label: 'Unpaid', color: 'red' };
  };

  // Calculate overdue days for unpaid invoices (local calendar days, tz-safe)
  const getOverdueDays = (invoiceDate: string) => {
    return getCalendarDaysPastDue(invoiceDate);
  };

  const handleViewInvoice = (invoice: any) => {
    if (invoice.type === 'booking') {
      setSelectedInvoice(invoice);
      setIsPaymentsModalOpen(true);
    } else if (invoice.type === 'product_sale') {
      if (invoice.invoice_id) {
        // Counter sales / orders carry a real standalone invoice — open the
        // exact same payments modal bookings use (send, history, PDF, terms).
        setStandaloneViewId(invoice.invoice_id);
      } else {
        // Legacy pre-invoice sales only have the product_sales row.
        setSelectedProductSaleForView(invoice);
        setIsProductPaymentsModalOpen(true);
      }
    }
  };

  const handleApplyPayment = (productSale: any) => {
    if (productSale.invoice_id) {
      // Invoice-backed sales settle through the standard payment modal so the
      // invoice rollup stays correct (legacy quick-pay bypasses the invoice).
      setSelectedInvoiceForPayment(productSale);
      return;
    }
    setSelectedProductSale(productSale);
    setIsApplyPaymentModalOpen(true);
  };

  const handleApplyBookingPayment = (booking: any) => {
    setSelectedBookingForPayment(booking);
    setIsRecordPaymentModalOpen(true);
  };

  const handlePaymentSuccess = () => {
    // Save current scroll position
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    sessionStorage.setItem('invoicesScrollPosition', scrollPosition.toString());
    
    // Refresh just the invoices data instead of the entire page
    fetchInvoiceData();
  };

  // Fetch transactions from database - MUST be before any early returns
  const fetchTransactions = React.useCallback(async () => {
    if (!selectedBusiness?.id) {
      setIsLoadingTransactions(false);
      return;
    }
    
    setIsLoadingTransactions(true);
    try {
      // Fetch transactions from database (instant loading)
      const { data: transactionsData, error } = await supabase
        .from('stripe_transactions')
        .select('*')
        .eq('business_id', selectedBusiness.id)
        .order('transaction_date', { ascending: false });

      if (error) throw error;

      // Fetch payouts for transactions that have payout_id
      const payoutIds = [...new Set((transactionsData || [])
        .filter((tx: any) => tx.payout_id)
        .map((tx: any) => tx.payout_id))];

      let payoutsMap = new Map();
      if (payoutIds.length > 0) {
        const { data: payouts } = await supabase
          .from('stripe_payouts')
          .select('id, stripe_payout_id, status, arrival_date')
          .in('id', payoutIds);

        if (payouts) {
          for (const payout of payouts) {
            payoutsMap.set(payout.id, payout);
          }
        }
      }
      
      // Format transactions to match expected structure
      const formattedTransactions = (transactionsData || []).map((tx: any) => {
        const payout = tx.payout_id ? payoutsMap.get(tx.payout_id) : null;
        return {
          id: tx.stripe_transaction_id,
          amount: tx.amount,
          currency: tx.currency,
          created: new Date(tx.transaction_date).getTime() / 1000, // Convert to Unix timestamp
          description: tx.description,
          fee: tx.fee,
          net: tx.net_amount,
          status: tx.status,
          type: tx.type,
          source: tx.source,
          payout: payout ? {
            payout_id: payout.id,
            stripe_payout_id: payout.stripe_payout_id,
            status: payout.status,
            arrival_date: payout.arrival_date,
          } : null,
          payout_status: tx.payout_status,
          payment_id: tx.payment_id,
          payment_intent_id: tx.payment_intent_id,
          transaction_date: tx.transaction_date,
        };
      });
      
      setTransactions(formattedTransactions);
      console.log(`✅ Loaded ${formattedTransactions.length} transactions from database`);
    } catch (error) {
      console.error('Error fetching transactions:', error);
      setTransactions([]); // Set empty array on error to prevent infinite loop
    } finally {
      setIsLoadingTransactions(false);
    }
  }, [selectedBusiness?.id]);

  // Sync transactions from Stripe to database, then refresh from database
  const handleSyncTransactions = React.useCallback(async () => {
    if (!selectedBusiness?.id) return;
    
    setIsSyncingTransactions(true);
    try {
      // Check if we have any transactions in database - if not, do full sync
      const { data: existingTx, error: checkError } = await supabase
        .from('stripe_transactions')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .limit(1)
        .maybeSingle();

      // If table doesn't exist or no transactions, do full sync
      const shouldFetchAll = checkError || !existingTx;
      
      console.log(`🔄 Starting sync: ${shouldFetchAll ? 'Full sync (no existing data)' : 'Smart sync (incremental)'}`);
      
      // Sync transactions to database
      const { data, error } = await supabase.functions.invoke('sync-stripe-transactions', {
        body: { 
          businessId: selectedBusiness.id,
          fetchAll: shouldFetchAll // Full sync if no data, smart sync if data exists
        }
      });

      if (error) {
        console.error('Sync error:', error);
        throw error;
      }
      
      console.log('Sync response:', data);
      
      // Refresh transactions from database after sync
      await fetchTransactions();
      
      // Show success message
      const message = shouldFetchAll 
        ? `Successfully synced ${data?.syncedCount || 0} transactions to database (initial sync)`
        : `Successfully synced ${data?.syncedCount || 0} new transactions to database`;
      alert(message);
    } catch (error: any) {
      console.error('Error syncing transactions:', error);
      const errorMessage = error?.message || 'Failed to sync transactions. Please try again.';
      alert(`Error: ${errorMessage}\n\nMake sure the stripe_transactions table exists (run migration first).`);
    } finally {
      setIsSyncingTransactions(false);
    }
  }, [selectedBusiness?.id, fetchTransactions]);

  // Filter transactions - MUST be before any early returns
  useEffect(() => {
    if (activeTab !== 'payouts') {
      setFilteredTransactions([]);
      return;
    }

    let filtered = [...transactions];

    // Apply date filter
    if (payoutDateFilter !== 'all-time') {
      const { start, end } = getDateRange(payoutDateFilter);
      filtered = filtered.filter(transaction => {
        // Handle both database format (transaction_date) and API format (created timestamp)
        let transactionDate: Date;
        if (transaction.transaction_date) {
          transactionDate = new Date(transaction.transaction_date);
        } else if (transaction.created) {
          transactionDate = new Date(transaction.created * 1000);
        } else {
          return false; // Skip if no date
        }
        
        // Compare dates at midnight to avoid timezone issues
        const transactionDateOnly = new Date(transactionDate.getFullYear(), transactionDate.getMonth(), transactionDate.getDate());
        const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
        const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());
        
        return transactionDateOnly >= startDateOnly && transactionDateOnly <= endDateOnly;
      });
    }

    // Apply status filter (filter by payout status)
    if (payoutStatusFilter !== 'all') {
      filtered = filtered.filter(transaction => {
        const payoutStatus = transaction.payout_status;
        // Map payout statuses
        if (payoutStatusFilter === 'pending') {
          return payoutStatus === 'pending';
        }
        return payoutStatus === payoutStatusFilter;
      });
    }

    setFilteredTransactions(filtered);
  }, [transactions, payoutDateFilter, payoutStatusFilter, activeTab]);

  // Load transactions when switching to payouts tab - MUST be before any early returns
  useEffect(() => {
    if (activeTab === 'payouts' && transactions.length === 0 && !isLoadingTransactions && selectedBusiness?.id) {
      fetchTransactions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedBusiness?.id]); // Only trigger on tab change or business change, not on transaction state changes

  // Switch to invoices tab if Stripe is not connected and we're on payouts tab
  useEffect(() => {
    const business = selectedBusiness as any;
    if (activeTab === 'payouts' && (!business?.stripe_connect_account_id || !business?.stripe_connect_charges_enabled)) {
      setActiveTab('invoices');
    }
  }, [activeTab, selectedBusiness]);

  if (isLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-sm font-medium text-gray-600">Loading invoices...</p>
        </div>
      </div>
    );
  }

  // Get payout status color
  const getPayoutStatusColor = (status: string) => {
    if (status === 'pending_payout') {
      return 'bg-yellow-50 text-yellow-700';
    }
    switch (status) {
      case 'paid':
        return 'bg-green-50 text-green-700';
      case 'in_transit':
        return 'bg-blue-50 text-blue-700';
      case 'pending':
        return 'bg-yellow-50 text-yellow-700';
      case 'failed':
        return 'bg-red-50 text-red-700';
      case 'canceled':
        return 'bg-gray-50 text-gray-700';
      default:
        return 'bg-gray-50 text-gray-700';
    }
  };

  // Format payout status label
  const formatPayoutStatus = (status: string) => {
    if (status === 'pending_payout') {
      return 'Pending Payout';
    }
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div 
      className="flex flex-col h-full overflow-y-auto bg-gray-50 min-h-screen" 
      data-invoices-page
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)'
      }}
    >
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200/60 px-4 py-3 sticky top-0 z-40" style={{
        fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
      }}>
        <div ref={invoiceTabsRef} className="flex bg-gray-100 rounded-[12px] p-1.5 gap-1.5 overflow-x-auto scrollbar-hide">
          <button
            data-tab-active={activeTab === 'invoices' ? 'true' : 'false'}
            onClick={() => setActiveTab('invoices')}
            className={`shrink-0 sm:flex-1 px-3 py-2.5 text-[15px] font-semibold rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation ${
              activeTab === 'invoices'
                ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
            style={{ 
              WebkitTapHighlightColor: 'transparent',
              letterSpacing: '-0.01em'
            }}
          >
            <div className="flex items-center justify-center space-x-2">
              <Receipt className="h-4 w-4" />
              <span>Invoices</span>
            </div>
          </button>
          <button
            data-tab-active={activeTab === 'payments' ? 'true' : 'false'}
            onClick={() => setActiveTab('payments')}
            className={`shrink-0 sm:flex-1 px-3 py-2.5 text-[15px] font-semibold rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation ${
              activeTab === 'payments'
                ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
          >
            <div className="flex items-center justify-center space-x-2">
              <DollarSign className="h-4 w-4" />
              <span>Payments</span>
            </div>
          </button>
          <button
            data-tab-active={activeTab === 'receivables' ? 'true' : 'false'}
            onClick={() => setActiveTab('receivables')}
            className={`shrink-0 sm:flex-1 px-3 py-2.5 text-[15px] font-semibold rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation ${
              activeTab === 'receivables'
                ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
          >
            <div className="flex items-center justify-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Receivables</span>
            </div>
          </button>
          {subscriptionsEnabled && (
            <button
              data-tab-active={activeTab === 'subscriptions' ? 'true' : 'false'}
              onClick={() => setActiveTab('subscriptions')}
              className={`shrink-0 sm:flex-1 px-3 py-2.5 text-[15px] font-semibold rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                activeTab === 'subscriptions'
                  ? 'bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
            >
              <div className="flex items-center justify-center space-x-2">
                <RefreshCw className="h-4 w-4" />
                <span>Subscriptions</span>
              </div>
            </button>
          )}
        </div>
      </div>

      {activeTab === 'invoices' ? (
        <InvoicesLedgerTab
          businessId={selectedBusiness?.id || ''}
          bookings={bookings}
          productSales={productSales}
          refreshKey={invoiceEditorRefreshKey}
          onNewInvoice={() => { setInvoiceEditorId(null); setIsInvoiceEditorOpen(true); }}
          onOpenStandaloneInvoice={(id, status) => {
            if (status === 'draft') { setInvoiceEditorId(id); setIsInvoiceEditorOpen(true); }
            else { setStandaloneViewId(id); }
          }}
          onOpenBookingInvoice={async (bookingId) => {
            // `bookings` only holds completed bookings, but draft invoices belong to
            // not-yet-completed bookings — fall back to fetching it directly.
            let bookingInvoice = bookings.find((b) => b.id === bookingId);
            if (!bookingInvoice) {
              try { bookingInvoice = await getBooking(bookingId); } catch { /* ignore */ }
            }
            if (bookingInvoice) {
              setSelectedInvoice({ ...bookingInvoice, type: 'booking' });
              setIsPaymentsModalOpen(true);
            }
          }}
          onRecordPayment={async (bookingId) => {
            let bookingInvoice = bookings.find((b) => b.id === bookingId);
            if (!bookingInvoice) {
              try { bookingInvoice = await getBooking(bookingId); } catch { /* ignore */ }
            }
            if (bookingInvoice) {
              setSelectedBookingForPayment(bookingInvoice);
              setIsRecordPaymentModalOpen(true);
            }
          }}
          onOpenProductSale={(sale) => {
            setSelectedProductSaleForView(sale);
            setIsProductPaymentsModalOpen(true);
          }}
          onApplyProductSalePayment={(sale) => {
            setSelectedProductSale(sale);
            setIsApplyPaymentModalOpen(true);
          }}
          onRecordStandalonePayment={(inv) => {
            // Standalone invoice (counter sale / order / manual) → the universal
            // payment modal keyed on invoice_id; it fetches the live balance itself.
            setSelectedInvoiceForPayment({
              invoice_id: inv.id,
              id: undefined,
              total_amount: (inv.total_cents || 0) / 100,
              customer_id: inv.customer_id || undefined,
              business_id: inv.business_id,
            });
          }}
        />
      ) : false ? (
        <>
          {/* Stats Summary */}
          <div className="grid grid-cols-3 gap-3 px-4 py-4">
            <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden min-w-0">
              <p className="text-[11px] font-medium text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>Invoices</p>
              <p className="text-[16px] font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.02em' }}>{filteredInvoices.length}</p>
            </div>
            
            <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden min-w-0">
              <p className="text-[11px] font-medium text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>Paid</p>
              <p className="text-[16px] font-bold text-green-600 truncate" style={{ letterSpacing: '-0.02em' }}>
                ${filteredInvoices
                  .filter(i => getPaymentStatus(i).status === 'paid')
                  .reduce((sum, i) => sum + (i.invoice_total || i.total_amount || 0), 0)
                  .toLocaleString()}
              </p>
            </div>
            
            <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden min-w-0">
              <p className="text-[11px] font-medium text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>Unpaid</p>
              <p className="text-[16px] font-bold text-red-600 truncate" style={{ letterSpacing: '-0.02em' }}>
                ${filteredInvoices
                  .filter(i => getPaymentStatus(i).status !== 'paid')
                  .reduce((sum, i) => sum + (i.invoice_total || i.total_amount || 0), 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>

      {/* Filters Section - iOS Style */}
      <div className="bg-white border-b border-gray-200/60 px-4 md:px-6 py-3 sticky top-0 z-30" style={{
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)'
      }}>
        <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2 w-full md:w-auto">
            {/* Select All / Deselect All */}
            {filteredInvoices.length > 0 && (
              <button
                onClick={toggleSelectAll}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-[13px] font-semibold rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation ${
                  selectedIds.size === filteredInvoices.length && filteredInvoices.length > 0
                    ? 'bg-blue-50 text-blue-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
              >
                {selectedIds.size === filteredInvoices.length && filteredInvoices.length > 0 ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                <span className="hidden sm:inline">
                  {selectedIds.size === filteredInvoices.length && filteredInvoices.length > 0 ? 'Deselect All' : `Select All (${filteredInvoices.length})`}
                </span>
              </button>
            )}

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="flex-1 md:flex-none md:w-auto appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150 touch-manipulation"
              style={{ 
                minHeight: '36px',
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: '-0.01em'
              }}
            >
              <option value="all">All Invoices</option>
              <option value="paid">Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>
          </div>
          
          {/* Date Filter */}
          <div className="flex items-center gap-2 w-full md:w-auto">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value as any)}
              className="flex-1 md:flex-none md:w-auto appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150 touch-manipulation"
              style={{ 
                minHeight: '36px',
                WebkitTapHighlightColor: 'transparent',
                letterSpacing: '-0.01em'
              }}
            >
              <option value="this-month">This Month</option>
              <option value="last-month">Last Month</option>
              <option value="this-year">This Year</option>
              <option value="last-year">Last Year</option>
              <option value="all-time">All Time</option>
              <option value="custom">Custom Range</option>
            </select>

            {dateFilter === 'custom' && (
              <>
                <input
                  type="date"
                  value={customDateStart}
                  onChange={(e) => setCustomDateStart(e.target.value)}
                  className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3 py-2.5 text-[13px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150 touch-manipulation"
                  style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                />
                <span className="text-[13px] text-gray-500 font-medium">to</span>
                <input
                  type="date"
                  value={customDateEnd}
                  onChange={(e) => setCustomDateEnd(e.target.value)}
                  className="appearance-none bg-gray-100 border-0 rounded-[10px] px-3 py-2.5 text-[13px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150 touch-manipulation"
                  style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
                />
              </>
            )}

            <button
              onClick={() => setIsStatementModalOpen(true)}
              className="flex items-center gap-1.5 px-3.5 py-2.5 text-[13px] font-semibold text-blue-700 bg-blue-50 rounded-[10px] hover:bg-blue-100 transition-all duration-150 active:scale-[0.98] touch-manipulation"
              style={{ minHeight: '36px', WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
            >
              <Send className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Send Statement</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="flex-1">
        {/* Invoices List */}
        <div className="px-4 py-6">
          
          {filteredInvoices.length === 0 ? (
            <div className="flex items-center justify-center h-64 px-4">
              <div className="text-center">
                <Receipt className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                <p className="text-[15px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                  No invoices found for selected date range
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2.5">
              {filteredInvoices.map((invoice, index) => {
                const paymentStatus = getPaymentStatus(invoice);
                const customer = invoice.customer;
                const totalAmount = invoice.invoice_total || invoice.total_amount || 0;
                const completedPayments = invoice.payments?.filter((p: any) => p.status === 'completed') || [];
                const totalPaid = completedPayments.reduce((sum: number, payment: any) => sum + payment.amount, 0);
                const balanceOwing = Math.round(Math.max(0, totalAmount - totalPaid) * 100) / 100;

                const isSelected = selectedIds.has(invoice.id);

                return (
                  <div
                    key={invoice.id}
                    className={`bg-white rounded-[12px] border shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.08)] active:scale-[0.98] cursor-pointer flex transition-all duration-150 ${
                      isSelected ? 'border-blue-400 ring-1 ring-blue-400/30' : 'border-gray-200/60'
                    }`}
                    onClick={() => handleViewInvoice(invoice)}
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                    }}
                  >
                    {/* Status Bar - Left Edge */}
                    <div className={`w-1 rounded-l-[12px] flex-shrink-0 ${
                      paymentStatus.color === 'green' ? 'bg-green-500' :
                      paymentStatus.color === 'orange' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`} />

                    {/* Checkbox */}
                    <div
                      className="flex items-center justify-center px-2 flex-shrink-0"
                      onClick={(e) => { e.stopPropagation(); toggleSelect(invoice.id); }}
                    >
                      <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-150 ${
                        isSelected
                          ? 'bg-blue-600 text-white'
                          : 'border-2 border-gray-300 bg-white'
                      }`}>
                        {isSelected && (
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                    
                    <div className="p-3.5 pl-1 flex-1 min-w-0 overflow-hidden">
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 mb-2.5">
                        <div className="flex items-center space-x-2.5 min-w-0 flex-1">
                          <div className={`h-8 w-8 rounded-full flex items-center justify-center text-[11px] font-semibold text-white flex-shrink-0 ${
                            invoice.type === 'product_sale' ? 'bg-blue-500' : 'bg-purple-500'
                          }`}>
                            {customer?.first_name?.[0]}{customer?.last_name?.[0]}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-[15px] font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                              {customer ? `${customer.first_name} ${customer.last_name}` : 'Unknown Customer'}
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5 truncate" style={{ letterSpacing: '-0.01em' }}>
                              #{invoice.invoice_number}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-[6px] ${
                            paymentStatus.color === 'green' ? 'bg-green-50 text-green-700' : 
                            paymentStatus.color === 'orange' ? 'bg-orange-50 text-orange-700' : 
                            'bg-red-50 text-red-700'
                          }`} style={{ letterSpacing: '-0.01em' }}>
                            {paymentStatus.label}
                          </span>
                          {paymentStatus.status !== 'paid' && (() => {
                            const overdueDays = getOverdueDays(invoice.invoice_date);
                            if (overdueDays > 0) {
                              return (
                                <p className="text-[11px] text-red-600 mt-0.5 font-medium" style={{ letterSpacing: '-0.01em' }}>
                                  {overdueDays} {overdueDays === 1 ? 'day' : 'days'} overdue
                                </p>
                              );
                            }
                            return null;
                          })()}
                        </div>
                      </div>

                      {/* Title/Type */}
                      <h2 className="text-[16px] font-semibold text-gray-900 mb-2.5" style={{ letterSpacing: '-0.01em' }}>
                        {invoice.type === 'product_sale' ? 'Product Sale' : 'Booking'}
                      </h2>

                      {/* Amount and Details */}
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-[20px] font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                            ${totalAmount.toLocaleString()}
                          </p>
                          {balanceOwing > 0 && (
                            <p className="text-[11px] text-red-600 mt-0.5 font-medium" style={{ letterSpacing: '-0.01em' }}>
                              Owing: ${balanceOwing.toLocaleString()}
                            </p>
                          )}
                          {totalPaid > 0 && (
                            <p className="text-[11px] text-green-600 mt-0.5 font-medium" style={{ letterSpacing: '-0.01em' }}>
                              Paid: ${totalPaid.toLocaleString()}
                            </p>
                          )}
                          {/* Show Stripe transaction if paid with Stripe */}
                          {(() => {
                            const stripePayments = completedPayments.filter((p: any) => p.stripe_payment_intent_id || p.stripe_checkout_session_id || p.stripe_balance_transaction_id);
                            if (stripePayments.length > 0) {
                              const firstStripePayment = stripePayments[0];
                              // Prefer balance transaction ID (same as payouts tab), fallback to payment intent ID
                              const transactionId = firstStripePayment.stripe_balance_transaction_id || 
                                                   firstStripePayment.stripe_payment_intent_id || 
                                                   firstStripePayment.stripe_checkout_session_id;
                              const shortId = transactionId ? transactionId.replace(/^(txn_|pi_|cs_)/, '').slice(-8) : 'Stripe';
                              return (
                                <div className="flex items-center space-x-1.5 mt-1.5 max-w-full">
                                  <img 
                                    src="/Stripe (2).png" 
                                    alt="Stripe" 
                                    className="h-3 w-3 object-contain flex-shrink-0"
                                  />
                                  <p className="text-[10px] text-gray-500 font-medium truncate" style={{ letterSpacing: '-0.01em' }}>
                                    {shortId}
                                  </p>
                                </div>
                              );
                            }
                            return null;
                          })()}
                        </div>
                        <div className="text-right flex flex-col items-end flex-shrink-0">
                          <p className="text-[11px] text-gray-500 mb-0.5" style={{ letterSpacing: '-0.01em' }}>
                            {formatCalendarDate(invoice.invoice_date, 'en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                          {/* Payment button for unpaid invoices */}
                          {(invoice.type === 'product_sale' || invoice.type === 'booking') && paymentStatus.status !== 'paid' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (invoice.type === 'product_sale') {
                                  handleApplyPayment(invoice);
                                } else {
                                  handleApplyBookingPayment(invoice);
                                }
                              }}
                              className="mt-2 inline-flex items-center justify-center px-3.5 py-2 text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm"
                              style={{ 
                                WebkitTapHighlightColor: 'transparent',
                                minHeight: '36px',
                                letterSpacing: '-0.01em'
                              }}
                            >
                              Apply Payment
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
        </>
      ) : activeTab === 'payments' ? (
        <PaymentsLedgerTab
          businessId={selectedBusiness?.id || ''}
          stripeConnected={!!(selectedBusiness as any)?.stripe_connect_account_id && !!(selectedBusiness as any)?.stripe_connect_charges_enabled}
          onSyncStripe={handleSyncTransactions}
          onOpenBookingInvoice={(bookingId) => {
            const bookingInvoice = bookings.find((b) => b.id === bookingId);
            if (bookingInvoice) {
              setSelectedInvoice({ ...bookingInvoice, type: 'booking' });
              setIsPaymentsModalOpen(true);
            }
          }}
        />
      ) : activeTab === 'receivables' ? (
        <AccountsReceivableTab
          businessId={selectedBusiness?.id || ''}
          bookings={bookings}
          refreshKey={arRefreshKey}
          onOpenBookingInvoice={(bookingId) => {
            const bookingInvoice = bookings.find((b) => b.id === bookingId);
            if (bookingInvoice) {
              setSelectedInvoice({ ...bookingInvoice, type: 'booking' });
              setIsPaymentsModalOpen(true);
            }
          }}
          onOpenStandaloneInvoice={(id) => setStandaloneViewId(id)}
          onReceivePayment={(id, name) => setReceivePaymentFor({ id, name })}
          onSendStatement={() => setIsStatementModalOpen(true)}
        />
      ) : activeTab === 'subscriptions' && subscriptionsEnabled ? (
        <div className="px-4 py-4">
          <SubscriptionsPanel />
        </div>
      ) : null}
      {false && (
        <>
          {/* Payouts Tab */}
          {/* Stats Summary for Payouts */}
          <div className="grid grid-cols-3 gap-3 px-4 py-4">
            <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden min-w-0">
              <p className="text-[11px] font-medium text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>Total Transactions</p>
              <p className="text-[16px] font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.02em' }}>{filteredTransactions.length}</p>
            </div>
            
            <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden min-w-0">
              <p className="text-[11px] font-medium text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>Paid Out</p>
              <p className="text-[16px] font-bold text-green-600 truncate" style={{ letterSpacing: '-0.02em' }}>
                ${filteredTransactions
                  .filter(t => t.payout_status === 'paid')
                  .reduce((sum, t) => sum + (t.net || 0), 0)
                  .toLocaleString()}
              </p>
            </div>
            
            <div className="bg-white rounded-[12px] p-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)] border border-gray-200/60 overflow-hidden min-w-0">
              <p className="text-[11px] font-medium text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>Pending</p>
              <p className="text-[16px] font-bold text-yellow-600 truncate" style={{ letterSpacing: '-0.02em' }}>
                ${filteredTransactions
                  .filter(t => t.payout_status === 'pending')
                  .reduce((sum, t) => sum + (t.net || 0), 0)
                  .toLocaleString()}
              </p>
            </div>
          </div>

          {/* Payouts Filters */}
          <div className="bg-white border-b border-gray-200/60 px-4 md:px-6 py-3 sticky top-[60px] z-30">
            <div className="flex flex-col space-y-3 md:space-y-0 md:flex-row md:items-center md:justify-between">
              {/* Status Filter */}
              <div className="relative w-full md:w-auto">
                <select
                  value={payoutStatusFilter}
                  onChange={(e) => setPayoutStatusFilter(e.target.value as any)}
                  className="w-full md:w-auto appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150 touch-manipulation"
                  style={{ 
                    minHeight: '36px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending Payout</option>
                  <option value="in_transit">In Transit</option>
                  <option value="paid">Paid Out</option>
                  <option value="failed">Failed</option>
                </select>
              </div>
              
              {/* Date Filter */}
              <div className="relative w-full md:w-auto">
                <select
                  value={payoutDateFilter}
                  onChange={(e) => setPayoutDateFilter(e.target.value as any)}
                  className="w-full md:w-auto appearance-none bg-gray-100 border-0 rounded-[10px] px-3.5 py-2.5 text-[15px] font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white transition-all duration-150 touch-manipulation"
                  style={{ 
                    minHeight: '36px',
                    WebkitTapHighlightColor: 'transparent',
                    letterSpacing: '-0.01em'
                  }}
                >
                  <option value="this-month">This Month</option>
                  <option value="last-month">Last Month</option>
                  <option value="this-year">This Year</option>
                  <option value="all-time">All Time</option>
                </select>
              </div>

              {/* Sync Button */}
              <button
                onClick={handleSyncTransactions}
                disabled={isSyncingTransactions}
                className="w-full md:w-auto inline-flex items-center justify-center px-4 py-2.5 text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{ 
                  WebkitTapHighlightColor: 'transparent',
                  minHeight: '36px',
                  letterSpacing: '-0.01em'
                }}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${isSyncingTransactions ? 'animate-spin' : ''}`} />
                {isSyncingTransactions ? 'Syncing...' : 'Sync Transactions'}
              </button>
            </div>
          </div>

          {/* Transactions List */}
          <div className="px-4 py-6">
            {isLoadingTransactions ? (
              <div className="flex items-center justify-center h-64 px-4">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                  <p className="text-[15px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                    Loading transactions...
                  </p>
                </div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="flex items-center justify-center h-64 px-4">
                <div className="text-center">
                  <Wallet className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                  <p className="text-[15px] font-medium text-gray-600" style={{ letterSpacing: '-0.01em' }}>
                    No transactions found
                  </p>
                  <p className="text-[13px] text-gray-500 mt-1" style={{ letterSpacing: '-0.01em' }}>
                    Click "Sync Transactions" to fetch from Stripe
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2.5">
                {filteredTransactions.map((transaction) => (
                  <div
                    key={transaction.id}
                    className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:shadow-[0_2px_4px_rgba(0,0,0,0.08)] active:scale-[0.98] cursor-pointer flex transition-all duration-150"
                    onClick={() => {
                      // Open payout details if transaction is in a payout
                      if (transaction.payout?.payout_id) {
                        setSelectedPayout({ id: transaction.payout.payout_id, stripe_payout_id: transaction.payout.stripe_payout_id });
                        setIsPayoutDetailsModalOpen(true);
                      }
                    }}
                    style={{
                      WebkitTapHighlightColor: 'transparent',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                    }}
                  >
                    {/* Status Bar - Left Edge */}
                    <div className={`w-1 rounded-l-[12px] flex-shrink-0 ${
                      transaction.payout_status === 'paid' ? 'bg-green-500' :
                      transaction.payout_status === 'in_transit' ? 'bg-blue-500' :
                      transaction.payout_status === 'pending' ? 'bg-yellow-500' :
                      transaction.payout_status === 'failed' ? 'bg-red-500' :
                      'bg-gray-500'
                    }`} />
                    
                    <div className="p-3.5 flex-1">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-2.5">
                        <div className="flex items-center space-x-2.5">
                          <img 
                            src="/Stripe (2).png" 
                            alt="Stripe" 
                            className="h-10 w-10 object-contain flex-shrink-0"
                          />
                          <div className="min-w-0 flex-1">
                            <h3 className="text-[15px] font-semibold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                              {transaction.description || 'Payment Transaction'} {transaction.id ? `#${transaction.id.slice(-8)}` : ''}
                            </h3>
                            <p className="text-[11px] text-gray-500 mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                              {transaction.payout 
                                ? `Payout: ${transaction.payout.stripe_payout_id?.slice(-8) || 'N/A'}` 
                                : 'Pending payout'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0 ml-2">
                          <span className={`inline-block px-2 py-0.5 text-[11px] font-semibold rounded-[6px] ${getPayoutStatusColor(transaction.payout_status)}`} style={{ letterSpacing: '-0.01em' }}>
                            {formatPayoutStatus(transaction.payout_status)}
                          </span>
                          {transaction.payout?.status && transaction.payout.status !== transaction.payout_status && (
                            <p className="text-[10px] text-gray-400 mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                              {transaction.payout.status}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Amount and Payout Info */}
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-[20px] font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                            ${(transaction.net || 0).toLocaleString()}
                          </p>
                          {transaction.fee > 0 && (
                            <p className="text-[11px] text-gray-500 mt-0.5" style={{ letterSpacing: '-0.01em' }}>
                              Fee: ${transaction.fee.toLocaleString()} • Gross: ${transaction.amount.toLocaleString()}
                            </p>
                          )}
                          {transaction.payout?.arrival_date && (
                            <p className="text-[11px] text-green-600 mt-0.5 font-medium" style={{ letterSpacing: '-0.01em' }}>
                              Arrives: {new Date(transaction.payout.arrival_date).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric'
                              })}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-[11px] text-gray-500 mb-0.5" style={{ letterSpacing: '-0.01em' }}>
                            {(() => {
                              const date = transaction.transaction_date 
                                ? new Date(transaction.transaction_date)
                                : new Date(transaction.created * 1000);
                              return date.toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              });
                            })()}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Floating Bulk Actions Bar */}
      {selectedIds.size > 0 && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 bg-white/95 rounded-2xl shadow-lg border border-gray-200/60"
          style={{
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
            maxWidth: 'calc(100vw - 32px)'
          }}
        >
          <span className="text-[14px] font-semibold text-gray-900 whitespace-nowrap" style={{ letterSpacing: '-0.01em' }}>
            {selectedIds.size} selected
          </span>

          <button
            onClick={handleBulkDownload}
            disabled={isBulkDownloading}
            className="flex items-center gap-1.5 px-4 py-2 text-[13px] font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-[10px] transition-all duration-150 active:scale-[0.98] touch-manipulation disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
            style={{ WebkitTapHighlightColor: 'transparent', letterSpacing: '-0.01em' }}
          >
            {isBulkDownloading ? (
              <>
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white/30 border-t-white" />
                <span>{bulkDownloadProgress}</span>
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" />
                <span>Download PDFs</span>
              </>
            )}
          </button>

          <button
            onClick={() => setSelectedIds(new Set())}
            className="flex items-center justify-center w-8 h-8 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-all duration-150 touch-manipulation"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* BetaPaymentsModal */}
      {receivePaymentFor && (
        <ReceivePaymentModal
          isOpen={!!receivePaymentFor}
          onClose={() => setReceivePaymentFor(null)}
          businessId={selectedBusiness?.id || ''}
          customerId={receivePaymentFor.id}
          customerName={receivePaymentFor.name}
          onSuccess={() => {
            setArRefreshKey((k) => k + 1);
            fetchInvoiceData();
          }}
        />
      )}

      {isInvoiceEditorOpen && selectedBusiness?.id && (
        <InvoiceEditorModal
          businessId={selectedBusiness.id}
          invoiceId={invoiceEditorId}
          onClose={() => { setIsInvoiceEditorOpen(false); setInvoiceEditorId(null); }}
          onSaved={() => setInvoiceEditorRefreshKey((k) => k + 1)}
          onIssued={(id) => setStandaloneViewId(id)}
        />
      )}

      {/* Standalone (booking-less) invoices open the same payments modal as booking invoices. */}
      {standaloneViewId && (
        <BetaPaymentsModal
          isOpen={!!standaloneViewId}
          onClose={() => setStandaloneViewId(null)}
          invoiceId={standaloneViewId}
          bookingId=""
          bookingNumber={0}
          bookingData={{ total_amount: 0 }}
          onUpdate={() => { setInvoiceEditorRefreshKey((k) => k + 1); handlePaymentSuccess(); }}
        />
      )}

      {/* Quick payment on an invoice-backed product sale — standard payment modal */}
      {selectedInvoiceForPayment && (
        <RecordPaymentModal
          isOpen={!!selectedInvoiceForPayment}
          onClose={() => setSelectedInvoiceForPayment(null)}
          onSuccess={() => { setSelectedInvoiceForPayment(null); handlePaymentSuccess(); }}
          onSendInvoice={() => {
            const invId = selectedInvoiceForPayment.invoice_id;
            setSelectedInvoiceForPayment(null);
            setStandaloneViewId(invId);
          }}
          invoiceId={selectedInvoiceForPayment.invoice_id}
          productSaleId={selectedInvoiceForPayment.id}
          businessId={selectedBusiness?.id || selectedInvoiceForPayment.business_id}
          bookingTotal={selectedInvoiceForPayment.total_amount || 0}
          amountPaid={0}
          customerId={selectedInvoiceForPayment.customer_id || undefined}
        />
      )}

      {selectedInvoice && selectedInvoice.type === 'booking' && (
        <BetaPaymentsModal
          isOpen={isPaymentsModalOpen}
          onClose={() => {
            setIsPaymentsModalOpen(false);
            setSelectedInvoice(null);
          }}
          bookingId={selectedInvoice.id}
          bookingNumber={selectedInvoice.booking_number}
          bookingData={{
            total_amount: selectedInvoice.total_amount,
            subtotal: selectedInvoice.subtotal,
            tax_amount: selectedInvoice.tax_amount,
            discount_amount: selectedInvoice.discount_amount,
            location: selectedInvoice.location,
            notes: selectedInvoice.notes,
            customer: selectedInvoice.customer,
            payments: selectedInvoice.payments,
            booking_services: selectedInvoice.booking_services
          }}
          booking={selectedInvoice}
          onUpdate={() => {
            // Save scroll position before refresh
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;
            sessionStorage.setItem('invoicesScrollPosition', scrollPosition.toString());
            // Refresh the invoices list using the dedicated function
            fetchInvoiceData();
          }}
        />
      )}

      {/* Product Payments Modal (Full View) */}
      {selectedProductSaleForView && (
        <ProductPaymentsModal
          isOpen={isProductPaymentsModalOpen}
          onClose={() => {
            setIsProductPaymentsModalOpen(false);
            setSelectedProductSaleForView(null);
          }}
          productSale={selectedProductSaleForView}
          onUpdate={() => {
            // Save scroll position before refresh
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;
            sessionStorage.setItem('invoicesScrollPosition', scrollPosition.toString());
            // Refresh the invoices list using the dedicated function
            fetchInvoiceData();
          }}
        />
      )}

      {/* Apply Product Sale Payment Modal (Quick Payment) */}
      {selectedProductSale && (
        <ApplyProductSalePaymentModal
          isOpen={isApplyPaymentModalOpen}
          onClose={() => {
            setIsApplyPaymentModalOpen(false);
            setSelectedProductSale(null);
          }}
          productSale={{
            ...selectedProductSale,
            business_id: selectedBusiness?.id || selectedProductSale.business_id
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {/* Record Booking Payment Modal */}
      {selectedBookingForPayment && (
        <RecordPaymentModal
          isOpen={isRecordPaymentModalOpen}
          onClose={() => {
            setIsRecordPaymentModalOpen(false);
            setSelectedBookingForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
          bookingId={selectedBookingForPayment.id}
          businessId={selectedBusiness?.id || ''}
          bookingTotal={selectedBookingForPayment.total_amount || 0}
          amountPaid={selectedBookingForPayment.payments?.reduce((sum: number, payment: any) => 
            payment.status === 'completed' ? sum + payment.amount : sum, 0
          ) || 0}
        />
      )}

      {/* Payout Details Modal */}
      {selectedPayout && (
        <PayoutDetailsModal
          isOpen={isPayoutDetailsModalOpen}
          onClose={() => {
            setIsPayoutDetailsModalOpen(false);
            setSelectedPayout(null);
          }}
          payout={selectedPayout}
        />
      )}

      {/* Send Statement Modal */}
      <SendStatementModal
        isOpen={isStatementModalOpen}
        onClose={() => setIsStatementModalOpen(false)}
        businessId={selectedBusiness?.id || ''}
        businessName={selectedBusiness?.name || ''}
        bookings={bookings}
      />
    </div>
  );
}